import { memo, useMemo, useCallback } from 'react';
import { Icon, Input } from 'hoi-poi-ui';
import { getLiteral } from 'utils/getLiteral';
import { CONDITION_OPERATOR_LOCALES } from '../../utils';
import SelectHoi from 'components/SelectHoi';

const TYPE_DEFAULTS = {
    text: 'in',
};

const AutomationBuilderCondition = memo(({ condition, onChange, onRemove, fields, operators }) => {
    const fieldType = useMemo(() => {
        const operator = Object.keys(condition || {})?.[0];
        const conditionField = condition[operator]?.[0]?.var;
        let type;

        for (const field of fields) {
            const option = field.options?.find((option) => option.value === conditionField);
            if (option) {
                type = option.type;
                break;
            }
        }

        return type;
    }, [condition, fields]);

    const operatorOptions = useMemo(() => {
        if (!operators) return [];

        if (!fieldType) return [];

        return operators[fieldType].map((operator) => ({
            label: getLiteral(CONDITION_OPERATOR_LOCALES[operator]),
            value: operator,
        }));
    }, [fieldType, operators]);

    const operatorValue = useMemo(() => Object.keys(condition || {})?.[0], [condition]);
    const fieldsValue = useMemo(
        () => condition[Object.keys(condition || {})?.[0]]?.[0]?.var,
        [condition],
    );

    const onChangeField = useCallback(
        (value, valueObj) => {
            if (!value) {
                onChange({});
            } else {
                let operator = Object.keys(condition || {})?.[0];
                // Getting first value type operator if no operator or current one not compatible
                if (!operator || !operators[valueObj?.type].find((op) => op === operator))
                    operator = TYPE_DEFAULTS[valueObj?.type] || operators[valueObj?.type]?.[0];

                onChange({
                    [operator]: [
                        {
                            var: value,
                        },
                    ],
                });
            }
        },
        [condition, onChange, operators],
    );

    const onChangeOperator = useCallback(
        (value) => {
            let operator = Object.keys(condition || {})?.[0];
            onChange({
                [value]: condition[operator],
            });
        },
        [condition, onChange],
    );

    const onChangeValue = useCallback(
        (value) => {
            let operator = Object.keys(condition || {})?.[0];
            onChange({
                [operator]: [condition[operator][0], value],
            });
        },
        [condition, onChange],
    );

    const typeInputEl = useMemo(() => {
        const value = condition[Object.keys(condition || {})?.[0]]?.[1];
        if (!fieldType) return null;
        switch (fieldType) {
            case 'text':
                return (
                    <Input
                        onChange={onChangeValue}
                        value={value}
                        placeholder={getLiteral('placeholder_text_field')}
                        isFullWidth
                    />
                );
        }
    }, [condition, fieldType, onChangeValue]);

    return (
        <div className="fm-automation-builder__condition">
            <div className="fm-automation-builder__condition-form">
                {fields?.length > 0 && (
                    <SelectHoi
                        placeholder={getLiteral('label_placeholder_field')}
                        onChange={onChangeField}
                        options={fields}
                        value={fieldsValue}
                        isFullWidth
                        useMenuPortal={false}
                        usePlainValue
                    />
                )}
                {fields?.length > 0 && (
                    <SelectHoi
                        options={operatorOptions}
                        onChange={onChangeOperator}
                        value={operatorValue}
                        isFullWidth
                        useMenuPortal={false}
                        isReadOnly={!operatorOptions?.length}
                        usePlainValue
                        isClearable={false}
                    />
                )}
                {typeInputEl}
            </div>
            <Icon
                className="fm-automation-builder__condition-form-icon"
                name="less"
                size="large"
                onClick={onRemove}
                options={operatorOptions}
            />
        </div>
    );
});

export default AutomationBuilderCondition;
